import { useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { Lock, Archive, ListChecks, Columns3, Flag, SlidersHorizontal, Pencil, Check, X, ChevronDown, ChevronRight, Users, RotateCcw, CheckCircle2, UserCog } from "lucide-react";
import { api, formatApiError, PROJECT_LIFECYCLE_LABELS, PROJECT_LIFECYCLE_STYLE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const QUICK = [
  { suffix: "backlog", label: "Backlog", icon: ListChecks },
  { suffix: "kanban", label: "Quadro Kanban", icon: Columns3 },
  { suffix: "sprints", label: "Sprints", icon: Flag },
  { suffix: "arquivadas", label: "Arquivadas", icon: Archive },
  { suffix: "configuracoes", label: "Configurações", icon: SlidersHorizontal },
];

const EDITABLE_LIFECYCLE = { planning: "Planejamento", in_progress: "Em andamento", paused: "Pausado" };
const emptyReport = { delivered: "", not_delivered: "", results: "", goal_met: "yes", learnings: "", notes: "" };

export default function OverviewPage() {
  const { projectId, project, sprints, members, reload } = useOutletContext();
  const { user } = useAuth();
  const isAdminMgr = !!project.can_admin;
  const [reassignSel, setReassignSel] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: project.name, description: project.description || "", project_status: project.project_status || "in_progress" });
  const [partsOpen, setPartsOpen] = useState(false);
  const [completeForm, setCompleteForm] = useState(null); // report object or null
  const [busy, setBusy] = useState(false);
  const activeSprint = (sprints || []).find((s) => s.status === "active");
  const openCount = project.open_tasks_count;
  const doneCount = project.done_tasks_count;
  const isCompleted = project.project_status === "completed";
  const isArchived = project.status === "archived";
  const report = project.completed_report || {};
  const fmt = (d) => d ? new Date(d).toLocaleDateString("pt-BR") : "—";

  const save = async () => {
    try { await api.patch(`/projects/${projectId}`, form); toast.success("Projeto atualizado."); setEditing(false); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doComplete = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/projects/${projectId}/complete`, { report: completeForm }); toast.success("Projeto concluído."); setCompleteForm(null); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const reopen = async () => { try { await api.post(`/projects/${projectId}/reopen`); toast.success("Projeto reaberto."); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  const archive = async () => { try { await api.post(`/projects/${projectId}/archive`); toast.success("Projeto arquivado."); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  const restore = async () => { try { await api.post(`/projects/${projectId}/restore`); toast.success("Projeto restaurado."); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  const doReassign = async () => {
    if (!reassignSel) { toast.error("Selecione um gerente."); return; }
    try { await api.post(`/projects/${projectId}/reassign-manager`, { user_id: reassignSel }); toast.success("Gerente atribuído."); setReassignSel(""); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const eligibleManagers = (members || []).filter((m) => m.status === "active");

  const goalLabel = { yes: "Sim", partial: "Parcialmente", no: "Não" }[report.goal_met] || "—";

  return (
    <div className="space-y-6" data-testid="project-overview-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{project.workspace_name}</div>
          {editing ? (
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="overview-name-input"
              className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-2 border-b border-slate-300 focus:outline-none w-full" />
          ) : (
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-2 flex items-center gap-2">
              {project.confidential && <Lock className="w-5 h-5 text-rose-500" />}
              {project.name}
            </h1>
          )}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-md ${PROJECT_LIFECYCLE_STYLE[project.project_status] || "bg-slate-100 text-slate-600"}`} data-testid="overview-status-badge">{PROJECT_LIFECYCLE_LABELS[project.project_status] || "Em andamento"}</span>
            {project.confidential && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md bg-rose-50 text-rose-700"><Lock className="w-3 h-3" /> Confidencial</span>}
            {isArchived && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-600"><Archive className="w-3 h-3" /> Arquivado</span>}
            {project.read_only && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md bg-blue-50 text-blue-700" data-testid="overview-readonly-badge">Somente leitura</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdminMgr && !editing && (
            <>
              {!isCompleted && !isArchived && (
                <button onClick={() => setCompleteForm({ ...emptyReport })} data-testid="overview-complete-btn" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"><CheckCircle2 className="w-4 h-4" /> Concluir projeto</button>
              )}
              {isCompleted && !isArchived && (
                <button onClick={reopen} data-testid="overview-reopen-btn" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 text-sm hover:bg-slate-50"><RotateCcw className="w-4 h-4" /> Reabrir</button>
              )}
              {!isArchived
                ? <button onClick={archive} data-testid="overview-archive-btn" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 text-sm hover:bg-slate-50"><Archive className="w-4 h-4" /> Arquivar</button>
                : <button onClick={restore} data-testid="overview-restore-btn" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 text-sm hover:bg-slate-50"><RotateCcw className="w-4 h-4" /> Restaurar</button>}
              {!isCompleted && !isArchived && (
                <button onClick={() => setEditing(true)} data-testid="overview-edit-btn" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 text-sm hover:bg-slate-50"><Pencil className="w-4 h-4" /> Editar</button>
              )}
            </>
          )}
          {editing && (
            <>
              <button onClick={save} data-testid="overview-save-btn" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-slate-900 text-white text-sm"><Check className="w-4 h-4" /> Salvar</button>
              <button onClick={() => { setEditing(false); setForm({ name: project.name, description: project.description || "", project_status: project.project_status || "in_progress" }); }} className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 text-sm"><X className="w-4 h-4" /> Cancelar</button>
            </>
          )}
        </div>
      </div>

      {project.reassignment_pending && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="reassignment-banner">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            <UserCog className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-amber-900">Reatribuição de Gerente pendente</div>
              <div className="text-xs text-amber-800">Responsável temporário: {project.temp_admin_name || "Administrador"}. Escolha um novo gerente elegível do espaço interdepartamental.</div>
            </div>
          </div>
          {user?.role === "admin" && (
            <div className="flex items-center gap-2 shrink-0">
              <select value={reassignSel} onChange={(e) => setReassignSel(e.target.value)} data-testid="reassign-manager-select"
                className="h-9 rounded-lg border border-amber-300 bg-white px-2 text-sm">
                <option value="">Selecionar gerente...</option>
                {eligibleManagers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <button onClick={doReassign} data-testid="reassign-manager-btn" className="h-9 px-3 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700">Atribuir</button>
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Descrição</h2>
        {editing ? (
          <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="overview-description-input" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        ) : (
          <p className="text-sm text-slate-600">{project.description || "Sem descrição."}</p>
        )}
        {editing && (
          <div className="mt-3">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Status</label>
            <select value={form.project_status} onChange={(e) => setForm({ ...form, project_status: e.target.value })} data-testid="overview-status-select" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm">
              {Object.entries(EDITABLE_LIFECYCLE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="overview-open"><div className="text-xs font-bold uppercase tracking-widest text-slate-500">Tarefas abertas</div><div className="mt-2 font-heading text-2xl font-semibold">{openCount ?? "—"}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="overview-done"><div className="text-xs font-bold uppercase tracking-widest text-slate-500">Concluídas</div><div className="mt-2 font-heading text-2xl font-semibold">{doneCount ?? "—"}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5"><div className="text-xs font-bold uppercase tracking-widest text-slate-500">Participantes</div><div className="mt-2 font-heading text-2xl font-semibold">{project.participants.length}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5"><div className="text-xs font-bold uppercase tracking-widest text-slate-500">Sprint ativa</div><div className="mt-2 text-sm font-medium text-slate-800 truncate">{activeSprint ? activeSprint.name : "Nenhuma"}</div></div>
      </div>

      {isCompleted && (
        <div className="bg-white border border-blue-200 rounded-xl shadow-sm p-6" data-testid="overview-completion-report">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-5 h-5 text-blue-600" />
            <h2 className="font-heading text-lg font-semibold">Relatório de conclusão</h2>
          </div>
          <dl className="space-y-3 text-sm">
            <ReportRow label="O que foi entregue" value={report.delivered} />
            <ReportRow label="O que não foi entregue" value={report.not_delivered} />
            <ReportRow label="Resultados" value={report.results} />
            <div><dt className="text-xs font-bold uppercase tracking-wider text-slate-500">Objetivo alcançado</dt><dd className="text-slate-700 mt-0.5">{goalLabel}</dd></div>
            <ReportRow label="Aprendizados" value={report.learnings} />
            <ReportRow label="Observações" value={report.notes} />
          </dl>
          <p className="text-xs text-slate-400 mt-4">Concluído por {report.completed_by_name || "—"} em {fmt(project.completed_at)}.</p>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Acessos rápidos</h2>
        <div className="flex flex-wrap gap-2">
          {QUICK.map((q) => (
            <Link key={q.suffix} to={`/projetos/${projectId}/${q.suffix}`} data-testid={`overview-quick-${q.suffix}`} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-200 text-sm hover:bg-slate-50">
              <q.icon className="w-4 h-4 text-slate-500" /> {q.label}
            </Link>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Detalhes</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-8 text-sm">
          <div className="flex justify-between"><dt className="text-slate-500">{project.workspace_type === "interdepartmental" ? "Espaço (E.I.)" : "Departamento"}</dt><dd className="text-slate-800">{project.workspace_name}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">Criado por</dt><dd className="text-slate-800" data-testid="overview-created-by">{project.created_by_name || "—"}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">Criado em</dt><dd className="text-slate-800">{fmt(project.created_at)}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">Participantes</dt><dd className="text-slate-800">{project.participants.length}</dd></div>
        </dl>

        {/* Expandable participants */}
        <div className="mt-4 border-t border-slate-100 pt-4">
          <button onClick={() => setPartsOpen((o) => !o)} data-testid="overview-participants-toggle"
            className="w-full flex items-center justify-between gap-2 text-sm font-medium text-slate-700 hover:text-slate-900">
            <span className="inline-flex items-center gap-2"><Users className="w-4 h-4 text-slate-500" /> Participantes — {project.participants.length} {project.participants.length === 1 ? "pessoa" : "pessoas"}</span>
            {partsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          {partsOpen && (
            <div className="mt-3" data-testid="overview-participants-list">
              {project.participants.length === 0 ? (
                <p className="text-sm text-slate-400">Nenhum participante.</p>
              ) : (
                <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
                  {project.participants.map((p) => (
                    <li key={p.user_id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`overview-participant-${p.user_id}`}>
                      <div className="w-8 h-8 rounded-full bg-slate-100 grid place-items-center text-xs font-semibold text-slate-600 shrink-0">
                        {(p.name || "?").slice(0, 1).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate">{p.name || p.user_id}</div>
                        <div className="text-xs text-slate-500 truncate">{p.department || project.workspace_name}</div>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 shrink-0">{p.project_role === "viewer" ? "Visualizador" : "Membro"}</span>
                    </li>
                  ))}
                </ul>
              )}
              {isAdminMgr && (
                <Link to={`/projetos/${projectId}/configuracoes`} data-testid="overview-manage-participants" className="inline-flex items-center gap-1.5 mt-3 text-sm text-slate-700 hover:text-slate-900 font-medium">
                  <SlidersHorizontal className="w-4 h-4" /> Gerenciar participantes
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Complete project modal */}
      {completeForm && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4 overflow-auto" onClick={() => setCompleteForm(null)} data-testid="complete-project-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={doComplete} className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-lg my-8 max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
              <h3 className="font-heading text-xl font-semibold">Concluir projeto</h3>
              <button type="button" onClick={() => setCompleteForm(null)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-4 space-y-4 overflow-y-auto">
              <p className="text-xs text-slate-500">A conclusão é bloqueada se houver sprint ativa, tarefas abertas ou checklist obrigatório pendente. Conclua/cancele a sprint e finalize as tarefas antes de encerrar.</p>
              <Field label="O que foi entregue" testid="report-delivered" value={completeForm.delivered} onChange={(v) => setCompleteForm({ ...completeForm, delivered: v })} />
              <Field label="O que não foi entregue" testid="report-not-delivered" value={completeForm.not_delivered} onChange={(v) => setCompleteForm({ ...completeForm, not_delivered: v })} />
              <Field label="Resultados" testid="report-results" value={completeForm.results} onChange={(v) => setCompleteForm({ ...completeForm, results: v })} />
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Objetivo alcançado?</label>
                <select value={completeForm.goal_met} onChange={(e) => setCompleteForm({ ...completeForm, goal_met: e.target.value })} data-testid="report-goal-met" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm">
                  <option value="yes">Sim</option>
                  <option value="partial">Parcialmente</option>
                  <option value="no">Não</option>
                </select>
              </div>
              <Field label="Aprendizados" testid="report-learnings" value={completeForm.learnings} onChange={(v) => setCompleteForm({ ...completeForm, learnings: v })} />
              <Field label="Observações" testid="report-notes" value={completeForm.notes} onChange={(v) => setCompleteForm({ ...completeForm, notes: v })} />
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 shrink-0">
              <button type="button" onClick={() => setCompleteForm(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Cancelar</button>
              <button type="submit" disabled={busy} data-testid="complete-project-confirm" className="h-10 px-4 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50">{busy ? "Concluindo..." : "Concluir projeto"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const ReportRow = ({ label, value }) => (
  <div><dt className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</dt><dd className="text-slate-700 mt-0.5 whitespace-pre-wrap">{value || "—"}</dd></div>
);

const Field = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
    <textarea rows={2} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
  </div>
);
